import { 
  getFirestore, 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc, 
  query, 
  orderBy, 
  serverTimestamp,
  limit
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';

// Predefined template variables that users can insert
export const DEFAULT_TEMPLATE_VARIABLES = [
  { key: 'clientName', label: 'Client Name', description: 'Full name of the client' },
  { key: 'firstName', label: 'First Name', description: 'Client\'s first name only' },
  { key: 'projectName', label: 'Project Name', description: 'Name of the project/service' },
  { key: 'amount', label: 'Amount', description: 'Invoice amount with currency' },
  { key: 'totalAmount', label: 'Total Amount', description: 'Total amount with currency' },
  { key: 'dueDate', label: 'Due Date', description: 'Payment due date' },
  { key: 'currency', label: 'Currency', description: 'Currency code (USD, EUR, etc.)' },
  { key: 'userName', label: 'Your Name', description: 'Your name or business name' },
  { key: 'businessName', label: 'Business Name', description: 'Your business name' },
  { key: 'invoiceNumber', label: 'Invoice Number', description: 'Unique invoice identifier' },
  { key: 'currentDate', label: 'Current Date', description: 'Today\'s date' },
  { key: 'paymentLink', label: 'Payment Link', description: 'Link to view and pay invoice' }
];

// Template categories for organization
export const TEMPLATE_CATEGORIES = [
  { id: 'general', name: 'General', icon: '' },
  { id: 'wedding', name: 'Wedding', icon: '' },
  { id: 'portrait', name: 'Portrait', icon: '' },
  { id: 'commercial', name: 'Commercial', icon: '' },
  { id: 'event', name: 'Event', icon: '' },
  { id: 'payment', name: 'Payment', icon: '' },
  { id: 'reminder', name: 'Reminder', icon: '' }
];

/**
 * Service for managing email templates in Firestore
 */
class EmailTemplateService {
  constructor() {
    this.db = getFirestore();
    this.auth = getAuth();
    this.collectionName = 'emailTemplates';
  }

  /**
   * Get user's templates collection reference
   */
  getUserTemplatesRef() {
    const currentUser = this.auth.currentUser;
    if (!currentUser) throw new Error('User not authenticated');
    
    return collection(this.db, 'profiles', currentUser.uid, this.collectionName);
  }

  /**
   * Create a new template
   */
  async createTemplate(templateData) {
    const currentUser = this.auth.currentUser;
    if (!currentUser) throw new Error('User not authenticated');

    const templatesRef = this.getUserTemplatesRef();
    
    const newTemplate = {
      name: templateData.name,
      content: templateData.content,
      category: templateData.category || 'general',
      description: templateData.description || '',
      variables: this.extractVariables(templateData.content),
      usageCount: 0,
      isDefault: false,
      userId: currentUser.uid,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(templatesRef, newTemplate);
    
    return {
      id: docRef.id,
      ...newTemplate,
      createdAt: new Date(),
      updatedAt: new Date()
    };
  }

  /**
   * Get all user templates
   */
  async getUserTemplates() {
    const templatesRef = this.getUserTemplatesRef();
    const q = query(templatesRef, orderBy('updatedAt', 'desc'));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate(),
      updatedAt: doc.data().updatedAt?.toDate()
    }));
  }

  /**
   * Get templates by category
   */
  async getTemplatesByCategory(category) {
    const templates = await this.getUserTemplates();
    return templates.filter(template => template.category === category);
  }

  /**
   * Get a specific template by ID
   */
  async getTemplate(templateId) {
    const currentUser = this.auth.currentUser;
    if (!currentUser) throw new Error('User not authenticated');

    const templateRef = doc(this.db, 'profiles', currentUser.uid, this.collectionName, templateId);
    const templateSnap = await getDoc(templateRef);
    
    if (!templateSnap.exists()) {
      throw new Error('Template not found');
    }

    return {
      id: templateSnap.id,
      ...templateSnap.data(),
      createdAt: templateSnap.data().createdAt?.toDate(),
      updatedAt: templateSnap.data().updatedAt?.toDate()
    };
  }

  /**
   * Update a template
   */
  async updateTemplate(templateId, templateData) {
    const currentUser = this.auth.currentUser;
    if (!currentUser) throw new Error('User not authenticated');

    const templateRef = doc(this.db, 'profiles', currentUser.uid, this.collectionName, templateId);
    
    const updateData = {
      name: templateData.name,
      content: templateData.content,
      category: templateData.category,
      description: templateData.description,
      variables: this.extractVariables(templateData.content),
      updatedAt: serverTimestamp()
    };

    await updateDoc(templateRef, updateData);
    
    // Return updated template
    return await this.getTemplate(templateId);
  }

  /**
   * Delete a template
   */
  async deleteTemplate(templateId) {
    const currentUser = this.auth.currentUser;
    if (!currentUser) throw new Error('User not authenticated');

    const templateRef = doc(this.db, 'profiles', currentUser.uid, this.collectionName, templateId);
    await deleteDoc(templateRef);
  }

  /**
   * Increment template usage count
   */
  async incrementUsage(templateId) {
    const currentUser = this.auth.currentUser;
    if (!currentUser) throw new Error('User not authenticated');

    const templateRef = doc(this.db, 'profiles', currentUser.uid, this.collectionName, templateId);
    const templateSnap = await getDoc(templateRef);
    
    if (templateSnap.exists()) {
      const currentUsage = templateSnap.data().usageCount || 0;
      await updateDoc(templateRef, {
        usageCount: currentUsage + 1,
        lastUsedAt: serverTimestamp()
      });
    }
  }

  /**
   * Duplicate a template
   */
  async duplicateTemplate(templateId) {
    const template = await this.getTemplate(templateId);
    const duplicatedTemplate = {
      ...template,
      name: `${template.name} (Copy)`,
      usageCount: 0,
      isDefault: false
    };
    
    delete duplicatedTemplate.id;
    delete duplicatedTemplate.createdAt;
    delete duplicatedTemplate.updatedAt;
    
    return await this.createTemplate(duplicatedTemplate);
  }

  /**
   * Replace variables in template content with actual values
   */
  replaceVariables(templateContent, variables) {
    let processedContent = templateContent;
    
    // Replace each variable with its value
    Object.entries(variables).forEach(([key, value]) => {
      const regex = new RegExp(`\\{${key}\\}`, 'g');
      processedContent = processedContent.replace(regex, value || '');
    });
    
    // Convert line breaks to HTML if not already HTML formatted
    if (!processedContent.includes('<p>') && !processedContent.includes('<br>')) {
      processedContent = processedContent
        .split('\n')
        .map(line => {
          const trimmedLine = line.trim();
          return trimmedLine ? `<p>${trimmedLine}</p>` : '<br/>';
        })
        .join('');
    }
    
    return processedContent;
  }

  /**
   * Extract variables from template content
   */
  extractVariables(templateContent) {
    const variablePattern = /\{([^}]+)\}/g;
    const variables = [];
    let match;
    
    while ((match = variablePattern.exec(templateContent)) !== null) {
      const variableName = match[1];
      if (!variables.includes(variableName)) {
        variables.push(variableName);
      }
    }
    
    return variables;
  }

  /**
   * Generate template preview with sample data
   */
  generatePreview(templateContent, sampleData = {}) {
    const defaultSampleData = {
      clientName: 'John Doe',
      firstName: 'John',
      projectName: 'Wedding Photography',
      amount: '$2,500.00',
      totalAmount: '$2,500.00',
      dueDate: 'December 15, 2024',
      currency: 'USD',
      userName: 'Your Name',
      businessName: 'Your Photography Business',
      invoiceNumber: 'INV-001',
      currentDate: new Date().toLocaleDateString(),
      paymentLink: '[View Invoice Button]'
    };

    const previewData = { ...defaultSampleData, ...sampleData };
    return this.replaceVariables(templateContent, previewData);
  }

  /**
   * Get most used templates
   */
  async getMostUsedTemplates(limitCount = 5) {
    const templatesRef = this.getUserTemplatesRef();
    const q = query(
      templatesRef, 
      orderBy('usageCount', 'desc'),
      limit(limitCount)
    );
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate(),
      updatedAt: doc.data().updatedAt?.toDate()
    }));
  }

  /**
   * Search templates by name or content
   */
  async searchTemplates(searchTerm) {
    const templates = await this.getUserTemplates();
    const lowerSearchTerm = searchTerm.toLowerCase();
    
    return templates.filter(template => 
      template.name.toLowerCase().includes(lowerSearchTerm) ||
      template.content.toLowerCase().includes(lowerSearchTerm) ||
      template.category.toLowerCase().includes(lowerSearchTerm)
    );
  }
}

// Export singleton instance
const emailTemplateService = new EmailTemplateService();
export default emailTemplateService; 